import React, { useState, useEffect, useRef, useCallback } from "react";
import client, { API, getToken, getActing } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Sparkles, TrendingUp, AlertTriangle, Target, Send, RefreshCw, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { DisqualifiedInsights } from "../../components/DisqualifiedInsights";
import { tr } from "../../i18n/LangContext";

const SUGGESTIONS = [
  "Which deal should I close first?",
  "What's stalling in my pipeline?",
  "Which agent is delivering best?",
  "Where am I overpaying?",
];

function Bubble({ role, text, streaming }) {
  const mine = role === "user";
  return (
    <div className={`flex ${mine ? "justify-end" : "justify-start"}`}>
      <div data-testid={`copilot-msg-${role}`}
        className={`max-w-[85%] whitespace-pre-wrap rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
          mine ? "bg-brand-red text-white" : "border border-white/10 bg-white/[0.04] text-slate-200"}`}>
        {text}
        {streaming && <span className="ml-0.5 inline-block h-4 w-1.5 animate-pulse bg-brand-green align-middle" />}
      </div>
    </div>
  );
}

export default function AIInsights() {
  const [insights, setInsights] = useState([]);
  const [generatedAt, setGeneratedAt] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState("");
  const [sending, setSending] = useState(false);
  const sessionId = useRef(`copilot_${Date.now().toString(36)}`);
  const scroller = useRef(null);

  const loadInsights = useCallback(async () => {
    try {
      const res = await client.get("/ai/insights");
      setInsights(res.data.items || []);
      setGeneratedAt(res.data.generated_at || null);
    } catch (e) { console.error("Insights load failed", e); }
  }, []);
  useEffect(() => { loadInsights(); }, [loadInsights]);

  useEffect(() => {
    if (scroller.current) scroller.current.scrollTop = scroller.current.scrollHeight;
  }, [messages, streaming]);

  const generate = async () => {
    setGenerating(true);
    try {
      const res = await client.post("/ai/insights");
      setInsights(res.data.items || []);
      setGeneratedAt(res.data.generated_at || null);
      toast.success(tr("Insights refreshed"));
    } catch (e) {
      toast.error(tr("Couldn't generate insights"), { description: e?.response?.data?.detail });
    } finally { setGenerating(false); }
  };

  const ask = async (question) => {
    const text = (question ?? input).trim();
    if (!text || sending) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text }]);
    setSending(true);
    setStreaming("");
    let answer = "";
    try {
      const acting = getActing();
      const res = await fetch(`${API}/ai/copilot`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
          ...(acting?.user_id ? { "X-Acting-As": acting.user_id } : {}),
        },
        body: JSON.stringify({ session_id: sessionId.current, message: text }),
      });
      if (!res.ok || !res.body) {
        const detail = await res.json().catch(() => ({}));
        throw new Error(detail.detail || "The copilot is unavailable.");
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop() || "";
        for (const part of parts) {
          const line = part.replace(/^data: /, "").trim();
          if (!line) continue;
          const evt = JSON.parse(line);
          if (evt.delta) { answer += evt.delta; setStreaming(answer); }
          if (evt.error) throw new Error(evt.error);
        }
      }
      setMessages((m) => [...m, { role: "assistant", text: answer }]);
    } catch (e) {
      toast.error(tr("Copilot failed"), { description: e.message });
      if (answer) setMessages((m) => [...m, { role: "assistant", text: answer }]);
    } finally {
      setStreaming("");
      setSending(false);
    }
  };

  const clearChat = async () => {
    try { await client.delete(`/ai/copilot/${sessionId.current}`); } catch (e) { console.error(e); }
    setMessages([]);
  };

  return (
    <div className="space-y-6">
      {/* Copilot */}
      <div className="relative overflow-hidden rounded-2xl border border-brand-red/20 bg-gradient-to-br from-brand-red/10 via-card/40 to-brand-green/10 p-6">
        <div className="absolute inset-0 tech-grid opacity-30" />
        <div className="relative">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
            <div className="grid h-12 w-12 shrink-0 place-items-center rounded-xl bg-brand-red/20">
              <Sparkles className="h-6 w-6 text-brand-red animate-pulse-glow" />
            </div>
            <div className="flex-1">
              <h2 className="font-display text-xl font-semibold text-white">{tr("Acquisitions Copilot")}</h2>
              <p className="text-sm text-muted-foreground">
                {tr("Ask anything about your pipeline — it reads your properties and agents before it answers.")}
              </p>
            </div>
            {messages.length > 0 && (
              <Button variant="outline" size="sm" onClick={clearChat} data-testid="copilot-clear"
                className="border-white/15 bg-white/[0.03] text-muted-foreground hover:text-white">
                <Trash2 className="h-3.5 w-3.5" /> Clear
              </Button>
            )}
          </div>

          {(messages.length > 0 || streaming) && (
            <div ref={scroller} data-testid="copilot-thread"
              className="mt-5 max-h-[420px] space-y-3 overflow-y-auto rounded-xl border border-white/8 bg-black/25 p-4">
              {messages.map((m, i) => <Bubble key={i} role={m.role} text={m.text} />)}
              {streaming && <Bubble role="assistant" text={streaming} streaming />}
              {sending && !streaming && (
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" /> Reading your pipeline…
                </div>
              )}
            </div>
          )}

          <div className="mt-5 flex items-center gap-2 rounded-xl border border-white/10 bg-black/30 p-1.5">
            <input value={input} onChange={(e) => setInput(e.target.value)} data-testid="copilot-input"
              onKeyDown={(e) => { if (e.key === "Enter") ask(); }}
              placeholder={tr("e.g. Which deal should I close first?")}
              className="w-full bg-transparent px-3 text-sm text-white outline-none placeholder:text-muted-foreground" />
            <Button size="sm" onClick={() => ask()} disabled={sending || !input.trim()} data-testid="copilot-send"
              className="bg-brand-red hover:bg-brand-redDark text-white disabled:opacity-40">
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            </Button>
          </div>

          {messages.length === 0 && !sending && (
            <div className="mt-3 flex flex-wrap gap-2">
              {SUGGESTIONS.map((q) => (
                <button key={q} onClick={() => ask(tr(q))} data-testid="copilot-suggestion"
                  className="rounded-full border border-white/12 bg-white/[0.03] px-3 py-1.5 text-xs text-muted-foreground transition-colors hover:border-brand-red/40 hover:text-white">
                  {tr(q)}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Insights */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="font-display text-lg font-semibold text-white">{tr("Pipeline insights")}</h3>
          <p className="text-xs text-muted-foreground">
            {generatedAt ? `${tr("Generated")} ${new Date(generatedAt).toLocaleString()}` : tr("Not generated yet")}
          </p>
        </div>
        <Button onClick={generate} disabled={generating} data-testid="generate-insights-btn"
          className="bg-brand-green hover:bg-brand-green/85 text-white disabled:opacity-50">
          {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          {generating ? tr("Reading your deals…") : insights.length ? tr("Refresh insights") : tr("Generate insights")}
        </Button>
      </div>

      {insights.length === 0 ? (
        <div data-testid="insights-empty" className="rounded-2xl border border-white/8 bg-white/[0.02] p-10 text-center">
          <Target className="mx-auto h-6 w-6 text-muted-foreground" />
          <p className="mt-3 text-sm text-muted-foreground">
            {tr("No insights yet. Generate them and Claude will read your whole pipeline — the deal to push, what's stalling, and where the money is.")}
          </p>
        </div>
      ) : (
        <div className="grid gap-5 lg:grid-cols-2">
          {insights.map((i) => {
            const green = i.tone === "green";
            const Icon = green ? TrendingUp : AlertTriangle;
            return (
              <div key={i.id} data-testid="insight-card"
                className={`rounded-2xl border p-5 ${green ? "border-brand-green/20 bg-brand-green/[0.04]" : "border-brand-red/20 bg-brand-red/[0.04]"}`}>
                <div className="flex items-center gap-2.5">
                  <div className={`grid h-9 w-9 shrink-0 place-items-center rounded-lg ${green ? "bg-brand-green/15" : "bg-brand-red/15"}`}>
                    <Icon className={`h-4 w-4 ${green ? "text-brand-green" : "text-brand-red"}`} />
                  </div>
                  <h3 className="font-display text-base font-semibold text-white">{i.title}</h3>
                </div>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{i.body}</p>
                <div className="mt-4">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-muted-foreground">{tr("Model confidence")}</span>
                    <span className={`font-semibold ${green ? "text-brand-green" : "text-brand-red"}`}>{i.confidence}%</span>
                  </div>
                  <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-white/8">
                    <div className="h-full rounded-full" style={{ width: `${i.confidence}%`, background: green ? "#1aa860" : "#e63946", transition: "width 0.8s ease" }} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <DisqualifiedInsights />
    </div>
  );
}
